import { t } from "i18next";
import * as Yup from "yup";

import { emailSchema } from "@/utils/UForms";

import type { TSection, TCompany } from "@/types";

const HEX_COLOR = /^#[0-9a-fA-F]{6}$/;

/**
 * A function, not a constant: `t()` is i18next's plain export, so the
 * caller must run this after i18next.init() (inside the component),
 * or every label resolves to its raw key.
 */
export const COMPANY_DETAILS_SECTIONS = ({
	normalizeWebsite,
	linkValueDisplay,
	withDefaultRate = false,
}: {
	normalizeWebsite: (value: string) => string;
	linkValueDisplay: (
		hrefPrefix: string | true,
		field: keyof TCompany
	) => (values: object) => React.ReactNode;
	/** A client's default daily rate, priced onto invoices drawn from a report. */
	withDefaultRate?: boolean;
}): TSection[] => [
	{
		key: "identity",
		title: t("company.groups.identity"),
		validationSchema: Yup.object().shape({
			name: Yup.string().trim().required(t("forms.required")),
			color: Yup.string().matches(HEX_COLOR, {
				message: t("forms.invalid"),
				excludeEmptyString: true,
			}),
		}),
		fields: [
			{
				name: "name",
				type: "input",
				label: t("company.name"),
				required: true,
			},
			{
				name: "legalName",
				type: "input",
				label: t("company.legalName"),
			},
			{
				name: "officerName",
				type: "input",
				label: t("company.officerName"),
			},
			{ name: "color", type: "color", label: t("company.color") },
			...(withDefaultRate
				? [
						{
							name: "defaultRate",
							type: "number" as const,
							label: t("company.billing.defaultRate"),
							min: 0,
							decimals: 2,
							displayProps: {
								info: {
									text: t("company.billing.defaultRateHint"),
								},
							},
						},
					]
				: []),
		],
	},
	{
		key: "contact",
		title: t("company.groups.contact"),
		validationSchema: Yup.object().shape({
			email: emailSchema(),
			phone: Yup.string().max(20, t("forms.max", { value: 20 })),
			// Validated as it will be sent (see `normalizeWebsite`), so
			// a bare domain passes here and gets its scheme on submit.
			website: Yup.string()
				.transform((value: string) =>
					value ? normalizeWebsite(value) : value
				)
				.url(t("forms.invalid")),
		}),
		fields: [
			{
				name: "email",
				type: "input",
				label: t("company.email"),
				valueDisplay: linkValueDisplay("mailto:", "email"),
			},
			{
				name: "phone",
				type: "input",
				label: t("company.phone"),
				valueDisplay: linkValueDisplay("tel:", "phone"),
			},
			{
				name: "website",
				type: "input",
				label: t("company.website"),
				valueDisplay: linkValueDisplay(true, "website"),
			},
		],
	},
	{
		key: "address",
		title: t("company.groups.address"),
		fields: [
			{
				name: "address",
				type: "input",
				label: t("company.address"),
			},
			{
				name: "addressExtra",
				type: "input",
				label: t("company.addressExtra"),
			},
			{
				name: "postalCode",
				type: "input",
				label: t("company.postalCode"),
			},
			{ name: "city", type: "input", label: t("company.city") },
			{
				name: "country",
				type: "input",
				label: t("company.country"),
			},
		],
	},
	{
		key: "legal",
		title: t("company.groups.legal"),
		fields: [
			{ name: "siren", type: "input", label: t("company.siren") },
			{
				name: "vatNumber",
				type: "input",
				label: t("company.vatNumber"),
			},
		],
	},
];
